let SHIPPING_RATES = {
  NL: { name: 'Netherlands', threshold: 3000, fee: 400 },
};

const SHIPPING_RATES_URL = window.PindooShippingRatesUrl || '/cdn/shop/t/3/assets/pindoo-shipping-rates.json';
const SHIPPING_RATES_UPDATED_EVENT = 'pindoo:shipping-rates';

const COUNTRY_STORAGE_KEY = 'pindooCartShippingCountry';
const COUNTRY_NAME_STORAGE_KEY = 'pindooCartShippingCountryName';
const COUNTRY_SOURCE_STORAGE_KEY = 'pindooCartShippingCountrySource';
const COUNTRY_STATE_STORAGE_KEY = 'pindooCartShippingCountryV2';
const COUNTRY_UPDATED_EVENT = 'pindoo:cart-shipping-country';
const GEOLOCATION_TIMEOUT = 5000;
const AUTO_COUNTRY_MAX_AGE = 24 * 60 * 60 * 1000;
const COUNTRY_RETRY_DELAY = 60 * 1000;

// Keep one atomic record, plus page state for browsers that block storage.
let pageCountry = null;
let countryLookupPromise = null;
let lastCountryLookupAt = null;

function isValidCountryState(state) {
  if (!state || !Object.hasOwn(SHIPPING_RATES, state.code)) return false;
  if (state.source === 'manual') return true;
  const age = Date.now() - state.updatedAt;
  return state.source === 'auto' && Number.isFinite(state.updatedAt) && age >= 0 && age < AUTO_COUNTRY_MAX_AGE;
}

function getCountryState() {
  if (isValidCountryState(pageCountry)) return pageCountry;

  try {
    const saved = localStorage.getItem(COUNTRY_STATE_STORAGE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      return isValidCountryState(state) ? state : null;
    }

    // Preserve explicit legacy choices, but never trust undated auto/default NL.
    const code = localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (localStorage.getItem(COUNTRY_SOURCE_STORAGE_KEY) === 'manual' && Object.hasOwn(SHIPPING_RATES, code)) {
      return { code, source: 'manual' };
    }
  } catch (_error) {
    // The page state still works when storage is unavailable or malformed.
  }
  return null;
}

function getStoredCountry() {
  return getCountryState()?.code || null;
}

function getStoredCountrySource() {
  return getCountryState()?.source || null;
}

function storeCountry(country, source) {
  if (!Object.hasOwn(SHIPPING_RATES, country)) return;
  pageCountry = { code: country, source, updatedAt: Date.now() };

  try {
    localStorage.setItem(COUNTRY_STATE_STORAGE_KEY, JSON.stringify(pageCountry));
    // Retain these mirrors for older open pages; V2 readers use only the record.
    localStorage.setItem(COUNTRY_STORAGE_KEY, country);
    localStorage.setItem(COUNTRY_NAME_STORAGE_KEY, SHIPPING_RATES[country].name);
    localStorage.setItem(COUNTRY_SOURCE_STORAGE_KEY, source);
  } catch (_error) {
    // Storage can be unavailable in private browsing; the visible estimate still updates.
  }
}

// Checkout reads the same page state instead of relying on writable storage.
window.PindooGetCartShippingCountry = () => {
  const code = getStoredCountry();
  return code ? { code, name: SHIPPING_RATES[code].name, source: getStoredCountrySource() } : null;
};

async function loadShippingRates() {
  try {
    const response = await fetch(SHIPPING_RATES_URL, { cache: 'no-store' });
    const data = await response.json();

    if (!Array.isArray(data?.countries)) return SHIPPING_RATES;

    SHIPPING_RATES = data.countries.reduce((rates, country) => {
      if (!country.code || !country.name) return rates;

      rates[country.code] = {
        name: country.name,
        threshold: Number(country.thresholdCents),
        fee: Number(country.feeCents),
      };

      return rates;
    }, {});

    window.dispatchEvent(new CustomEvent(SHIPPING_RATES_UPDATED_EVENT));
  } catch (_error) {
    // Keep the Netherlands fallback if the local JSON asset is unavailable.
  }

  return SHIPPING_RATES;
}

const shippingRatesPromise = loadShippingRates();

async function fetchCountry(url, readCountry) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), GEOLOCATION_TIMEOUT);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const data = await response.json();
    const detected = data?.success === false ? null : readCountry(data);
    const countryCode = typeof detected === 'string' ? detected.toUpperCase() : '';
    return /^[A-Z]{2}$/.test(countryCode) ? countryCode : null;
  } catch (_error) {
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
}

async function preloadShippingCountry() {
  await shippingRatesPromise;

  const storedCountry = getStoredCountry();

  if (storedCountry) return storedCountry;
  if (countryLookupPromise) return countryLookupPromise;
  if (lastCountryLookupAt !== null && Date.now() - lastCountryLookupAt < COUNTRY_RETRY_DELAY) return null;

  lastCountryLookupAt = Date.now();
  countryLookupPromise = (async () => {
    const root = window.Shopify?.routes?.root || '/';
    const shopifyCountry = await fetchCountry(
      `${root}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false`,
      (data) => data?.detected_values?.country?.handle
    );
    if (getStoredCountrySource() === 'manual') return getStoredCountry();
    const detectedCountry = shopifyCountry || await fetchCountry('https://ipwho.is/', (data) => data?.country_code);
    // A detected unsupported destination must not be replaced by another country.
    const country = Object.hasOwn(SHIPPING_RATES, detectedCountry) ? detectedCountry : null;
    if (getStoredCountrySource() === 'manual') return getStoredCountry();

    if (country) storeCountry(country, 'auto');
    window.dispatchEvent(new CustomEvent(COUNTRY_UPDATED_EVENT, { detail: { country } }));
    return country;
  })();

  try {
    return await countryLookupPromise;
  } finally {
    countryLookupPromise = null;
  }
}

preloadShippingCountry();
window.addEventListener('online', () => {
  lastCountryLookupAt = null;
  preloadShippingCountry();
});

class PindooCartShippingEstimator extends HTMLElement {
  connectedCallback() {
    this.initializeAttempts = 0;
    this.scheduleInitialize();
  }

  disconnectedCallback() {
    this.countryButton?.removeEventListener('click', this.handleCountryButtonClick);
    this.countryInput?.removeEventListener('input', this.handleCountryInput);
    this.countryInput?.removeEventListener('focus', this.handleCountryFocus);
    this.countryInput?.removeEventListener('click', this.handleCountryFocus);
    this.countryInput?.removeEventListener('keydown', this.handleCountryKeydown);
    this.countryList?.removeEventListener('mousedown', this.handleCountryListMouseDown);
    this.countryList?.removeEventListener('click', this.handleCountryListClick);
    this.countryList?.removeEventListener('keydown', this.handleCountryListKeydown);
    this.removeEventListener('focusout', this.handleFocusOut);
    window.removeEventListener(COUNTRY_UPDATED_EVENT, this.handlePreloadedCountry);
    window.removeEventListener(SHIPPING_RATES_UPDATED_EVENT, this.handleShippingRatesUpdate);
    window.clearTimeout(this.initializeTimer);
  }

  scheduleInitialize() {
    window.clearTimeout(this.initializeTimer);
    this.initializeTimer = window.setTimeout(() => this.initialize(), 0);
  }

  initialize() {
    if (this.initialized) return;

    this.countryWrapper = this.querySelector('[data-pindoo-shipping-country]');
    this.countryButton = this.querySelector('[data-pindoo-shipping-country-button]');
    this.countryInput = this.querySelector('[data-pindoo-shipping-country-input]');
    this.countryList = this.querySelector('[data-pindoo-shipping-country-list]');
    this.shippingValue = this.querySelector('[data-pindoo-shipping-value]');
    this.freeShipping = this.querySelector('[data-pindoo-free-shipping]');
    this.note = this.querySelector('[data-pindoo-shipping-note]');
    this.totalValue = this.parentElement?.querySelector('[data-pindoo-estimated-total]');

    if (!this.countryInput || !this.countryList || !this.shippingValue || !this.totalValue) {
      this.initializeAttempts += 1;

      if (this.initializeAttempts <= 10) {
        this.initializeTimer = window.setTimeout(() => this.initialize(), 50);
      }

      return;
    }

    this.initialized = true;
    this.countries = Object.entries(SHIPPING_RATES).sort(([, a], [, b]) => a.name.localeCompare(b.name));
    this.renderCountryOptions('');
    this.countryButton?.addEventListener('click', this.handleCountryButtonClick);
    this.countryInput.addEventListener('input', this.handleCountryInput);
    this.countryInput.addEventListener('focus', this.handleCountryFocus);
    this.countryInput.addEventListener('click', this.handleCountryFocus);
    this.countryInput.addEventListener('keydown', this.handleCountryKeydown);
    this.countryList.addEventListener('mousedown', this.handleCountryListMouseDown);
    this.countryList.addEventListener('click', this.handleCountryListClick);
    this.countryList.addEventListener('keydown', this.handleCountryListKeydown);
    this.addEventListener('focusout', this.handleFocusOut);
    window.addEventListener(COUNTRY_UPDATED_EVENT, this.handlePreloadedCountry);
    window.addEventListener(SHIPPING_RATES_UPDATED_EVENT, this.handleShippingRatesUpdate);
    this.resolveInitialCountry();
  }

  renderCountryOptions(filterValue) {
    const query = filterValue.trim().toLowerCase();
    const filteredCountries = this.countries.filter(([code, country]) => {
      return country.name.toLowerCase().includes(query) || code.toLowerCase().includes(query);
    });
    const fragment = document.createDocumentFragment();

    for (const [code, country] of filteredCountries) {
      const option = document.createElement('button');
      option.className = 'pindoo-cart-shipping__country-option';
      option.type = 'button';
      option.role = 'option';
      option.dataset.countryCode = code;
      option.textContent = country.name;
      option.setAttribute('aria-selected', code === this.selectedCountry ? 'true' : 'false');
      fragment.append(option);
    }

    if (filteredCountries.length === 0) {
      const empty = document.createElement('span');
      empty.className = 'pindoo-cart-shipping__country-empty';
      empty.textContent = 'No countries found';
      fragment.append(empty);
    }

    this.countryList.replaceChildren(fragment);
  }

  openCountryList() {
    this.renderCountryOptions(this.countryInput.value);
    this.countryWrapper?.setAttribute('data-country-open', 'true');
    this.countryList.hidden = false;
    this.countryButton?.setAttribute('aria-expanded', 'true');
    this.countryInput.setAttribute('aria-expanded', 'true');
  }

  closeCountryList() {
    this.countryWrapper?.removeAttribute('data-country-open');
    this.countryList.hidden = true;
    this.countryButton?.setAttribute('aria-expanded', 'false');
    this.countryInput.setAttribute('aria-expanded', 'false');
  }

  resolveInitialCountry() {
    this.setCountry(getStoredCountry());

    shippingRatesPromise.then(() => {
      this.handleShippingRatesUpdate();
    });

    preloadShippingCountry().then((country) => {
      if (getStoredCountrySource() === 'manual') return;
      this.setCountry(country);
    });
  }

  handleCountryChange = () => {
    const country = this.selectedCountry;
    if (!country) return;

    storeCountry(country, 'manual');
    this.updateEstimate(country);
  };

  handleCountryInput = () => {
    this.openCountryList();
  };

  handleCountryButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.countryList.hidden) {
      this.closeCountryList();
      return;
    }

    this.countryInput.value = '';
    this.openCountryList();
  };

  handleCountryFocus = () => {
    const currentValue = this.countryInput.value.trim().toLowerCase();
    const isShowingCountryName = Object.values(SHIPPING_RATES).some((country) => {
      return country.name.toLowerCase() === currentValue;
    });

    if (isShowingCountryName) {
      this.countryInput.value = '';
    }

    this.openCountryList();
  };

  handleCountryKeydown = (event) => {
    const options = Array.from(this.countryList.querySelectorAll('.pindoo-cart-shipping__country-option'));

    if (event.key === 'Escape') {
      event.stopPropagation();
      this.closeCountryList();
      this.countryInput.value = SHIPPING_RATES[this.selectedCountry]?.name || '';
      return;
    }

    if (event.key === 'ArrowDown' && options[0]) {
      event.preventDefault();
      event.stopPropagation();
      options[0].focus();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      const firstOption = options[0];
      if (firstOption) this.chooseCountry(firstOption.dataset.countryCode);
    }
  };

  handleCountryListMouseDown = (event) => {
    if (event.target.closest?.('.pindoo-cart-shipping__country-option')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  handleCountryListClick = (event) => {
    const option = event.target.closest?.('.pindoo-cart-shipping__country-option');
    if (!option) return;

    event.preventDefault();
    event.stopPropagation();
    this.chooseCountry(option.dataset.countryCode);
  };

  handleCountryListKeydown = (event) => {
    const options = Array.from(this.countryList.querySelectorAll('.pindoo-cart-shipping__country-option'));
    const currentIndex = options.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeCountryList();
      this.countryInput.focus();
      return;
    }

    if ((event.key === 'Enter' || event.key === ' ') && currentIndex >= 0) {
      event.preventDefault();
      event.stopPropagation();
      this.chooseCountry(options[currentIndex].dataset.countryCode);
      return;
    }

    if (event.key === 'ArrowDown' && currentIndex >= 0) {
      event.preventDefault();
      event.stopPropagation();
      options[Math.min(currentIndex + 1, options.length - 1)]?.focus();
      return;
    }

    if (event.key === 'ArrowUp' && currentIndex >= 0) {
      event.preventDefault();
      event.stopPropagation();

      if (currentIndex === 0) {
        this.countryInput.focus();
      } else {
        options[currentIndex - 1]?.focus();
      }
    }
  };

  handleFocusOut = () => {
    window.setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      this.closeCountryList();
      this.countryInput.value = SHIPPING_RATES[this.selectedCountry]?.name || '';
    }, 0);
  };

  chooseCountry(country) {
    if (!SHIPPING_RATES[country]) return;

    this.setCountry(country);
    storeCountry(country, 'manual');
    this.closeCountryList();
    this.countryButton?.focus({ preventScroll: true });
  }

  handlePreloadedCountry = (event) => {
    if (getStoredCountrySource() === 'manual') return;

    const country = event.detail?.country;
    this.setCountry(country);
  };

  handleShippingRatesUpdate = () => {
    this.countries = Object.entries(SHIPPING_RATES).sort(([, a], [, b]) => a.name.localeCompare(b.name));
    this.renderCountryOptions(this.countryInput?.value || '');
    this.setCountry(getStoredCountry());
  };

  setCountry(country) {
    const normalizedCountry = Object.hasOwn(SHIPPING_RATES, country) ? country : null;
    this.selectedCountry = normalizedCountry;
    const countryName = SHIPPING_RATES[normalizedCountry]?.name || '';
    if (this.countryInput) this.countryInput.value = countryName;
    if (this.countryButton) this.countryButton.textContent = countryName || 'Select country';
    if (this.countryList) this.renderCountryOptions('');
    this.updateEstimate(normalizedCountry);
  }

  updateEstimate(country) {
    const rate = SHIPPING_RATES[country];
    const subtotalCents = Number.parseInt(this.dataset.cartTotalCents || '0', 10);

    if (!rate || Number.isNaN(subtotalCents)) {
      this.shippingValue.textContent = 'At checkout';
      this.totalValue.textContent = this.formatMoney(subtotalCents || 0);
      this.totalValue.setAttribute('value', this.formatMoney(subtotalCents || 0));
      if (this.freeShipping) this.freeShipping.textContent = '';
      this.freeShipping?.setAttribute('hidden', '');
      if (this.note) this.note.textContent = 'Select a shipping country to see your estimate.';
      return;
    }

    const shippingCents = subtotalCents >= rate.threshold ? 0 : rate.fee;
    const estimatedTotalCents = subtotalCents + shippingCents;
    const amountForFreeShipping = Math.max(rate.threshold - subtotalCents, 0);

    this.shippingValue.textContent = shippingCents === 0 ? 'Free' : this.formatMoney(shippingCents);
    this.totalValue.textContent = this.formatMoney(estimatedTotalCents);
    this.totalValue.setAttribute('value', this.formatMoney(estimatedTotalCents));

    if (this.freeShipping) {
      if (amountForFreeShipping > 0) {
        this.freeShipping.textContent = `Add ${this.formatMoney(amountForFreeShipping)} more for free shipping.`;
        this.freeShipping.removeAttribute('hidden');
      } else {
        this.freeShipping.textContent = 'Free shipping unlocked.';
        this.freeShipping.removeAttribute('hidden');
      }
    }

    if (this.note) {
      this.note.textContent = 'Final shipping is confirmed at checkout.';
    }
  }

  formatMoney(cents) {
    const currency = this.dataset.currencyCode || 'EUR';
    const amount = cents / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (_error) {
      return `€${amount.toFixed(2)}`;
    }
  }
}

if (!customElements.get('pindoo-cart-shipping-estimator')) {
  customElements.define('pindoo-cart-shipping-estimator', PindooCartShippingEstimator);
}

window.queueMicrotask(() => {
  document.querySelectorAll('pindoo-cart-shipping-estimator').forEach((estimator) => {
    estimator.scheduleInitialize?.();
  });
});
